import { hostOf, NAVIGATION_ACTIONS } from './_shared.js';
import { resolveWorkspacePath, WORKSPACE_PATHS } from './paths.js';
import { isObject, isoToMs, writeJsonl } from './writer-utils.js';

// ============================================================================
// timeline/steps.jsonl
// ============================================================================

export interface RunStepLike {
  id: string;
  nodeId: string;
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
  errorMessage: string | null;
  orderIndex: number;
  metadataJson: Record<string, unknown> | null | unknown;
  /** Human-readable name resolved from the workflow node. Caller computes this. */
  nodeName: string | null;
  /** Node type from the workflow definition. Caller resolves this. */
  nodeType: string | null;
}

export const writeTimelineSteps = async (
  workspaceRoot: string,
  steps: ReadonlyArray<RunStepLike>
): Promise<void> => {
  const lines = steps
    .slice()
    .sort((a, b) => a.orderIndex - b.orderIndex)
    .map((step) => {
      const meta = isObject(step.metadataJson) ? step.metadataJson : {};
      const startedMs = isoToMs(step.startedAt);
      const finishedMs = isoToMs(step.finishedAt);
      return {
        id: step.id,
        nodeId: step.nodeId,
        orderIndex: step.orderIndex,
        name: step.nodeName,
        nodeType: step.nodeType,
        status: step.status,
        startedAt: step.startedAt,
        finishedAt: step.finishedAt,
        startedAtMs: startedMs,
        finishedAtMs: finishedMs,
        durationMs: startedMs !== null && finishedMs !== null ? finishedMs - startedMs : null,
        errorMessage: step.errorMessage,
        cacheOutcome: meta.cacheOutcome ?? null,
        cacheHitSource: meta.cacheHitSource ?? null,
        cacheProcessingStatus: meta.cacheProcessingStatus ?? null,
        cacheOutcomeReason: meta.cacheOutcomeReason ?? null,
      };
    });
  await writeJsonl(resolveWorkspacePath(workspaceRoot, WORKSPACE_PATHS.timelineSteps), lines);
};

// ============================================================================
// timeline/actions.jsonl + navigation.jsonl
// ============================================================================

export interface ActionTimelineEntry {
  action: string;
  label?: string;
  target?: string;
  value?: string;
  code?: string;
  error?: string;
  url?: string;
  /** Relative ms from `startTimestamp`. */
  timestampMs: number;
  success: boolean;
  isAnnotation?: boolean;
  /** Annotation classifier (e.g. `crash-recovery`) when `isAnnotation` is true. */
  annotationType?: string;
}

export interface ParsedTimeline {
  /** ISO baseline for the entries' relative `timestampMs`. */
  startTimestamp?: string;
  entries: ActionTimelineEntry[];
}

export const writeTimelineActionsAndNavigation = async (
  workspaceRoot: string,
  timeline: ParsedTimeline
): Promise<void> => {
  const baselineMs = timeline.startTimestamp ? Date.parse(timeline.startTimestamp) : null;

  const actions = timeline.entries.map((e) => ({
    action: e.action,
    label: e.label ?? null,
    target: e.target ?? null,
    value: e.value ?? null,
    code: e.code ?? null,
    error: e.error ?? null,
    url: e.url ?? null,
    relativeTimestampMs: e.timestampMs,
    timestampMs: baselineMs !== null ? baselineMs + e.timestampMs : null,
    success: e.success,
    isAnnotation: e.isAnnotation ?? false,
    // e.g. `crash-recovery` — lets the agent `jq` mid-run browser crashes. Omitted
    // for ordinary actions so it doesn't bloat every row.
    ...(e.annotationType ? { annotationType: e.annotationType } : {}),
  }));

  await writeJsonl(resolveWorkspacePath(workspaceRoot, WORKSPACE_PATHS.timelineActions), actions);

  const navRows: Array<{
    relativeTimestampMs: number;
    timestampMs: number | null;
    action: string;
    from: string | null;
    to: string | null;
    crossHost: boolean;
  }> = [];
  let prevUrl: string | null = null;
  for (const a of actions) {
    const isNavAction = NAVIGATION_ACTIONS.has(a.action);
    const urlChanged = a.url !== null && prevUrl !== null && a.url !== prevUrl;
    if (!isNavAction && !urlChanged) {
      if (a.url) prevUrl = a.url;
      continue;
    }
    const fromHost = hostOf(prevUrl);
    const toHost = hostOf(a.url);
    navRows.push({
      relativeTimestampMs: a.relativeTimestampMs,
      timestampMs: a.timestampMs,
      action: a.action,
      from: prevUrl,
      to: a.url,
      crossHost: fromHost !== null && toHost !== null && fromHost !== toHost,
    });
    if (a.url) prevUrl = a.url;
  }

  await writeJsonl(
    resolveWorkspacePath(workspaceRoot, WORKSPACE_PATHS.timelineNavigation),
    navRows
  );
};
